import { locateTrackPosition, type Track } from '../track/TrackLoader';

export interface WrongWayConfig {
  /** Below this speed (m/s) the detector idles — a stopped car shouldn't warn. */
  minSpeed: number;
  /** Normalized dot product threshold to enter the wrong-way state. */
  enterDot: number;
  /** Normalized dot product threshold to clear it (hysteresis vs enterDot). */
  clearDot: number;
  /** Sustained-time before activating the warning, in seconds. */
  enterDelay: number;
  /** Sustained-time before clearing the warning, in seconds. */
  clearDelay: number;
}

const DEFAULTS: WrongWayConfig = {
  minSpeed: 4,
  enterDot: -0.35,
  clearDot: 0.1,
  enterDelay: 0.9,
  clearDelay: 0.3,
};

/**
 * Detects when the racer is moving against the track's local forward tangent.
 *
 * Compares the racer's velocity to the tangent at the nearest centerline
 * point. Uses hysteresis + delay so that brief spins, slides, and reversing
 * out of a wall don't trip the warning.
 */
export class WrongWayDetector {
  private active = false;
  private enterAccum = 0;
  private clearAccum = 0;
  private readonly config: WrongWayConfig;

  constructor(private readonly track: Track, config: Partial<WrongWayConfig> = {}) {
    this.config = { ...DEFAULTS, ...config };
  }

  get isWrongWay(): boolean {
    return this.active;
  }

  reset(): void {
    this.active = false;
    this.enterAccum = 0;
    this.clearAccum = 0;
  }

  update(px: number, pz: number, vx: number, vz: number, dt: number): void {
    const speed = Math.hypot(vx, vz);
    if (speed < this.config.minSpeed) {
      this.enterAccum = 0;
      this.clearAccum += dt;
      if (this.active && this.clearAccum >= this.config.clearDelay) {
        this.active = false;
      }
      return;
    }

    const tp = locateTrackPosition(this.track, px, pz);
    const dot = (vx * tp.tx + vz * tp.tz) / speed;

    if (this.active) {
      if (dot > this.config.clearDot) {
        this.clearAccum += dt;
        if (this.clearAccum >= this.config.clearDelay) {
          this.active = false;
          this.clearAccum = 0;
          this.enterAccum = 0;
        }
      } else {
        this.clearAccum = 0;
      }
    } else {
      if (dot < this.config.enterDot) {
        this.enterAccum += dt;
        if (this.enterAccum >= this.config.enterDelay) {
          this.active = true;
          this.enterAccum = 0;
          this.clearAccum = 0;
        }
      } else {
        this.enterAccum = 0;
      }
    }
  }
}
